import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Stepper, Step, StepLabel } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { useParams } from "react-router-dom";
import {
  UpfrontPaymentFinancialQuestionsWrapper,
  UpfrontPaymentFinancialStepperWrapper,
  UpfrontPaymentMedicaidWarningWrapper,
} from "../UpfrontPayment.styles";
import {
  GetUpfrontPaymentFinancialAssistance,
  UpfrontPaymenFinancialAssistanceResponse,
  Bill,
} from "../Services/UpfrontPaymentService";
import {
  ProfileModalButtons,
  ProfileModalTitle,
} from "../../shared/Modal/Modal.styles";
import { Button } from "../../shared/Button/Button";
import { useDeviceHook } from "../../../utils/hooks/useDeviceHook";
import ProfileInsuranceUpload from "../../Profile/ProfileInsuranceUpload";
import { PatientInsuranceInfo } from "../../Profile/helpers";
import { SpinnerLoading } from "../../shared/Spinner/SpinnerLoading";
import { StepConfirmation } from "./StepConfirmation";
import { getStepIconComponent } from "./utils";
import { BillingSupportNumber } from "../types";
import { InsuranceUploadType } from "../../Insurance/types";

interface MedicaidFlowProps {
  setMedicaidStatus: React.Dispatch<React.SetStateAction<"Yes" | "No" | null>>;
  setUpfrontBill: React.Dispatch<React.SetStateAction<Bill | undefined>>;
  setFinancialAidResponse: React.Dispatch<
    React.SetStateAction<UpfrontPaymenFinancialAssistanceResponse | undefined>
  >;
}

export const MedicaidFlow: React.FC<MedicaidFlowProps> = ({
  setMedicaidStatus,
  setUpfrontBill,
  setFinancialAidResponse,
}) => {
  const { deviceType } = useDeviceHook();
  const { t } = useTranslation();
  const { billingPK, adminID } = useParams<{
    billingPK: string;
    adminID?: string;
  }>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Upload Documents", "Confirmation"];
  const [patientInsuranceInfo, setPatientInsuranceInfo] =
    React.useState<PatientInsuranceInfo>({
      billToInsuranceType: "Upload",
      insuranceCompanyName: "",
      groupId: "",
      memberId: "",
      policyOwnerName: "",
      policyOwnerDob: "",
      relationshipToPolicyOwnerSelf: true,
      relationshipToPolicyOwnerDependent: false,
      relationshipToPolicyOwnerOther: false,
      insuranceFileUrls: {},
      checkError: false,
    });

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => {
    if (activeStep === 0) {
      setMedicaidStatus(null);
      setActiveStep(0);
      return;
    }
    setActiveStep((prev) => prev - 1);
  };
  const onSubmit = async () => {
    setLoading(true);
    const financialAssistanceResponse =
      await GetUpfrontPaymentFinancialAssistance(
        billingPK,
        {
          has_medicaid: true,
          household_size: 0,
          income: 0,
          medicaid_insurance_card_urls: Object.values(
            patientInsuranceInfo.insuranceFileUrls,
          ).map((file) => file.s3Url),
          extenuating_circumstances: [],
          locality: null,
        },
        null,
        adminID,
      );
    setLoading(false);
    if (financialAssistanceResponse?.ok) {
      const financialAssistanceJson =
        (await financialAssistanceResponse.json()) as UpfrontPaymenFinancialAssistanceResponse;
      if (financialAssistanceJson) {
        setFinancialAidResponse(financialAssistanceJson);
        setUpfrontBill(financialAssistanceJson.bill);
      }
    } else {
      setError(
        t("upfrontPayment.financialAssistance.errors.submit", {
          phone: BillingSupportNumber,
        }),
      );
    }
  };

  const blockNext = () => {
    switch (activeStep) {
      case 0:
        return Object.keys(patientInsuranceInfo.insuranceFileUrls).length < 2;
      default:
        return false;
    }
  };

  const isLastStep = activeStep === steps.length - 1;
  const isSubmitting = loading && isLastStep;

  const getButtonLabel = () => {
    if (isSubmitting) return "";
    if (isLastStep) return t("upfrontPayment.actions.submit");
    return t("upfrontPayment.actions.next");
  };

  const renderStepContent = () => {
    if (loading && !isLastStep) {
      return <SpinnerLoading />;
    }
    if (error) {
      return <div>{error}</div>;
    }
    switch (activeStep) {
      case 0:
        return (
          <>
            <UpfrontPaymentMedicaidWarningWrapper>
              {t("upfrontPayment.financialAssistance.medicaidNoCost")}
            </UpfrontPaymentMedicaidWarningWrapper>

            <ProfileInsuranceUpload
              patientInsuranceInfo={patientInsuranceInfo}
              setPatientInsuranceInfo={setPatientInsuranceInfo}
              insuranceType={InsuranceUploadType.medicaid_or_hmo}
            />
          </>
        );
      case 1:
        return (
          <StepConfirmation
            medicaidInfo={{
              hasMedicaid: "Yes",
              frontCardFileName: patientInsuranceInfo.insuranceFileUrls.front
                ? t("upfrontPayment.financialAssistance.frontCardUploaded")
                : "",
              backCardFileName: patientInsuranceInfo.insuranceFileUrls.back
                ? t("upfrontPayment.financialAssistance.backCardUploaded")
                : "",
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <UpfrontPaymentFinancialQuestionsWrapper>
        {renderStepContent()}
        <UpfrontPaymentFinancialStepperWrapper marginTop={0}>
          <Stepper>
            {steps.map((value, index) => (
              <Step key={value}>
                <StepLabel
                  StepIconComponent={getStepIconComponent(index, activeStep)}
                />
              </Step>
            ))}
          </Stepper>
        </UpfrontPaymentFinancialStepperWrapper>
      </UpfrontPaymentFinancialQuestionsWrapper>
      <ProfileModalTitle deviceType={deviceType}>
        <ProfileModalButtons deviceType={deviceType}>
          <Button
            className="upfront-cancel-button"
            label={t("upfrontPayment.actions.back")}
            onClick={handleBack}
            size="medium"
            reverse
            textColor="greyMedium"
            backgroundColor="greyMedium"
          />
          <Button
            className="upfront-submit-button"
            label={getButtonLabel()}
            data-testid="submit-button"
            disabled={blockNext() || loading}
            onClick={isLastStep ? onSubmit : handleNext}
            size="medium"
            backgroundColor="greenDark"
            startIcon={
              isSubmitting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : undefined
            }
          />
        </ProfileModalButtons>
      </ProfileModalTitle>
    </>
  );
};
